/**
 * 68.17: a topographic overlay on the commander's view (the owner, Oct 10: "play with the idea of a topo overlay on
 * diorama map"). Contour lines draped on the real terrain — every `step` (the corner topo map's own interval, ui/topoMap.ts
 * `contourStep`, the same heights), a heavier index line every 5th, the bands faintly tinted —
 * with the topo map's own line weights (0.72 a line, 0.5 an index line), so the table and the corner map read as one.
 *
 * One term in the terrain's fragment shader (the planet mesh's and the fine ground's material): the height is the
 * distance from the planet's centre less the base of its range, read from the drawn surface; the lines are anti-aliased
 * by the height's screen derivative. No geometry. Its strength (0 … 1) comes from the commander's view (lane D's stops:
 * in at the squad stop, crisp at the diorama, out on the way back to the shoulder) and its toggle (l0Keys `topo`, the
 * Lab's Topo overlay row, off by default); at 0 the term is skipped by a uniform branch. Presentation only.
 */
import * as THREE from 'three';
import type { PlanetState } from '../types';

/** The shared uniforms every terrain material reads (one viewed planet at a time). */
export const TOPO_UNIFORMS = {
  uTopo: { value: 0 },
  uTopoCentre: { value: new THREE.Vector3() },
  uTopoBase: { value: 0 },
  uTopoStep: { value: 1 },
  /** 62.61: the step being faded to, and how far (0 … 1): a change of the lines' spacing cross-fades, never jumps */
  uTopoStep2: { value: 1 },
  uTopoMix: { value: 0 },
};

/** The topo map's line weights: a contour darkens the ground to this share, an index line (every 5th) to that. */
export const TOPO_LINE = 0.72, TOPO_INDEX = 0.5;

/**
 * Add the contour term to a terrain material (keeps any shader hook it already has). `transformed` is the drawn
 * surface (after a material's own lift), so the lines sit on what is drawn.
 */
export function applyTopoOverlay(material: THREE.Material): void {
  const before = material.onBeforeCompile?.bind(material);
  material.onBeforeCompile = (shader, renderer) => {
    before?.(shader, renderer);
    Object.assign(shader.uniforms, TOPO_UNIFORMS);
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vTopoWorld;')
      .replace('#include <project_vertex>', '#include <project_vertex>\n  vTopoWorld = (modelMatrix * vec4(transformed, 1.0)).xyz;');
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>
varying vec3 vTopoWorld;
uniform float uTopo;
uniform vec3 uTopoCentre;
uniform float uTopoBase;
uniform float uTopoStep;
uniform float uTopoStep2;
uniform float uTopoMix;
// the ground's shade at a contour step: the line (an index line every 5th, darker) over the bands' faint tint
float topoShade(float s) {
  float band = (length(vTopoWorld - uTopoCentre) - uTopoBase) / s;
  float w = max(fwidth(band), 1e-4);
  float d = abs(fract(band + 0.5) - 0.5);                       // 0 on a contour, 0.5 midway between
  float line = 1.0 - smoothstep(0.6 * w, 1.6 * w, d);
  float index = step(abs(mod(floor(band + 0.5), 5.0)), 0.5);    // every 5th
  float dark = mix(${TOPO_LINE.toFixed(2)}, ${TOPO_INDEX.toFixed(2)}, index);
  float tint = 1.0 + 0.035 * (mod(floor(band), 2.0) * 2.0 - 1.0);   // the bands, faintly
  return tint * mix(1.0, dark, line);
}`)
      .replace('#include <color_fragment>', `#include <color_fragment>
  if (uTopo > 0.001) {
    // 62.61: the step's change cross-faded (uTopoMix 0: the one step, as before)
    float shade = topoShade(uTopoStep);
    if (uTopoMix > 0.0) shade = mix(shade, topoShade(uTopoStep2), uTopoMix);
    diffuseColor.rgb *= mix(1.0, shade, uTopo);
  }`);
  };
  const key = material.customProgramCacheKey?.bind(material);
  material.customProgramCacheKey = () => `${key ? key() : ''}|topo`;
  material.needsUpdate = true;
}

/**
 * The overlay on `planet` at `strength` (0 … 1), each frame: its centre, and the step — the corner topo map's own
 * (ui/topoMap.ts `contourStep`, so its lines and the table's are the same heights), bands counted from the planet's radius
 * as the map counts them.
 */
export function setTopoOverlay(planet: Pick<PlanetState, 'position' | 'radius'> | null, strength: number, step: number, dt = 0): void {
  TOPO_UNIFORMS.uTopo.value = planet && step > 0 ? Math.max(0, Math.min(1, strength)) : 0;
  if (!planet || strength <= 0.001 || !(step > 0)) { topoFade.key = ''; return; }
  TOPO_UNIFORMS.uTopoCentre.value.set(planet.position.x, planet.position.y, planet.position.z);
  TOPO_UNIFORMS.uTopoBase.value = planet.radius;
  // 62.61 (the owner: "some pop in out with terrain and topo"): the lines' spacing follows the corner map's step and the
  // density's divisor, both in steps — so a change cross-fades over TOPO_FADE_S instead of every line jumping at once;
  // another planet (or the overlay off and on) starts at its step
  const key = `${planet.position.x},${planet.position.y},${planet.position.z},${planet.radius}`;
  const f = topoFade;
  if (key !== f.key) { f.key = key; f.from = f.to = step; f.t = 1; }
  else if (step !== f.to) { f.from = f.t < 0.5 ? f.from : f.to; f.to = step; f.t = 0; }
  if (f.t < 1) f.t = Math.min(1, f.t + Math.max(0, dt) / TOPO_FADE_S);
  if (f.t >= 1) f.from = f.to;
  const m = f.t >= 1 ? 0 : f.t * f.t * (3 - 2 * f.t);
  TOPO_UNIFORMS.uTopoStep.value = f.from;
  TOPO_UNIFORMS.uTopoStep2.value = f.to;
  TOPO_UNIFORMS.uTopoMix.value = m;
}
/** 62.61: how long a change of the topo's step takes to cross-fade (seconds). */
export const TOPO_FADE_S = 0.6;
const topoFade = { key: '', from: 0, to: 0, t: 1 };

/**
 * 68.17's follow-up (the owner, trying it on a flat world: "probably would want it to be closer segments"): the overlay's
 * density. The corner map draws about a dozen lines across its window's relief, never finer than half a person, so on
 * flat ground it shows two or three. The overlay then divides the map's step by 5, and by 25 on the flattest, until at
 * least `TOPO_LINES_MIN` lines cross that relief. At a fifth, its index lines (every 5th) are exactly the map's lines; on
 * rough ground it keeps the map's step. `density` is the Lab's dial: 'auto', or the divisor itself.
 */
export type TopoDensity = 'auto' | 1 | 5 | 25;
export const TOPO_LINES_MIN = 10;
export function overlayStep(mapStep: number, range: number, density: TopoDensity = 'auto'): number {
  if (!(mapStep > 0)) return mapStep;
  if (density !== 'auto') return mapStep / density;
  let k = 1;
  while (k < 25 && range / (mapStep / k) < TOPO_LINES_MIN) k *= 5;
  return mapStep / k;
}

let densityOverride: TopoDensity | null = null;
/** The Lab's Topo density row (its launch); null goes back to the page's `?topodensity=` / auto. */
export function setTopoDensity(d: TopoDensity | null): void { densityOverride = d; }
export function topoDensity(): TopoDensity {
  if (densityOverride !== null) return densityOverride;
  try {
    const q = typeof location !== 'undefined' ? new URLSearchParams(location.search).get('topodensity') : null;
    return q === '1' ? 1 : q === '5' ? 5 : q === '25' ? 25 : 'auto';
  } catch { return 'auto'; }
}

let topoOverride: boolean | null = null;
/** The Lab's Topo overlay row (its launch); null goes back to the page's `?topo=1` / off. */
export function setTopoEnabled(on: boolean | null): void { topoOverride = on; }
/** Whether a match starts with the overlay on (L toggles it in play). */
export function topoEnabled(): boolean {
  if (topoOverride !== null) return topoOverride;
  try { return typeof location !== 'undefined' && new URLSearchParams(location.search).get('topo') === '1'; } catch { return false; }
}
